import {motion} from 'framer-motion';
import {Card, DataStatus, Segment, SegmentProps, Tooltip} from 'platform/components';
import {
  Box,
  Clickable,
  getCssSize,
  Heading,
  Icon,
  Stack,
  HStack,
  Show,
  Space,
  Text,
  VStack,
} from 'platform/foundation';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {always} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetPurchaseFunnelQuery, useGetSaleFunnelQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {usePermissions} from '@dms/shared';

import {TestIdProps} from 'shared/web';

import {SEGMENT_MIN_WIDTH} from '../consts/segmentMinWidth';
import {OwnerSegment} from '../hooks/useRedirectFromOpportunities/types/OwnerSegment';
import {TypeSegment} from '../hooks/useRedirectFromOpportunities/types/TypeSegment';
import {useRedirectFromOpportunities} from '../hooks/useRedirectFromOpportunities/useRedirectFromOpportunities';
import {createOpportunitiesFunnelColumns} from '../utils/createOpportunitiesFunnelColumns';
import {AnimatedFunnelValue} from './AnimatedFunnelValue';

const SALES_FUNNEL_GRAPH_BAR_HEIGHT = 34;
const SALES_FUNNEL_GRAPH_BAR_HEIGHT_MOBILE = 6;
interface OpportunitiesFunnelProps extends TestIdProps {
  branchId: string | undefined;
  isFunnelInterestsEnabled: boolean;
  isFunnelBusinessCasesEnabled: boolean;
}

export function OpportunitiesFunnel(props: OpportunitiesFunnelProps) {
  const [ownerSegment, setOwnerSegment] = useState<OwnerSegment>('total');
  const [typeSegment, setTypeSegment] = useState<TypeSegment>('selling');

  const [handleRedirectFromOpportunity, isRedirecting] = useRedirectFromOpportunities({
    branchId: props.branchId,
    ownerSegment,
    typeSegment,
  });

  const [canReadInterest] = usePermissions({
    permissionKeys: ['interestRead'],
    scopes: {
      interestRead: {
        // BE should handle participation scope regarding opportunities funnel
        participation: 'SKIP',
        // BE should handle branch scope regarding opportunities funnel
        branchId: 'SKIP',
      },
    },
  });

  const {
    data: purchaseFunnelData,
    isLoading: isPurchaseFunnelLoading,
    isError: isPurchaseFunnelError,
  } = useGetPurchaseFunnelQuery(
    {
      branchId: props.branchId,
      onlyMine: ownerSegment === 'my',
    },
    {skip: typeSegment === 'selling', refetchOnMountOrArgChange: true}
  );

  const {
    data: saleFunnelData,
    isLoading: isSaleFunnelLoading,
    isError: isSaleFunnelError,
  } = useGetSaleFunnelQuery(
    {
      branchId: props.branchId,
      onlyMine: ownerSegment === 'my',
    },
    {skip: typeSegment === 'buying', refetchOnMountOrArgChange: true}
  );

  const isLoading = match(typeSegment)
    .with('selling', always(isSaleFunnelLoading))
    .with('buying', always(isPurchaseFunnelLoading))
    .otherwise(always(false));

  const isError = match(typeSegment)
    .with('selling', always(isSaleFunnelError))
    .with('buying', always(isPurchaseFunnelError))
    .otherwise(always(false));

  const columns = match(typeSegment)
    .with(
      'selling',
      always(
        createOpportunitiesFunnelColumns(
          saleFunnelData,
          props.isFunnelInterestsEnabled,
          props.isFunnelBusinessCasesEnabled,
          canReadInterest,
          typeSegment
        )
      )
    )
    .with(
      'buying',
      always(
        createOpportunitiesFunnelColumns(
          purchaseFunnelData,
          props.isFunnelInterestsEnabled,
          props.isFunnelBusinessCasesEnabled,
          canReadInterest,
          typeSegment
        )
      )
    )
    .otherwise(always([]));

  return (
    <Card>
      <VStack spacing={4}>
        <Stack direction={['column', 'row']}>
          <Heading size={4}>{i18n.t('page.homepage.labels.currentOpportunities')}</Heading>
          <Show onTablet onNotebook onDesktop>
            <Space fillAvailable />
          </Show>
          <Stack direction={['column', 'row']} spacing={2}>
            <Segment
              value={ownerSegment}
              options={ownerSegments}
              onChange={(segment) => setOwnerSegment(segment)}
              minWidth={SEGMENT_MIN_WIDTH}
            />
            <Segment
              value={typeSegment}
              options={typeSegments}
              onChange={(segment) => setTypeSegment(segment)}
              minWidth={SEGMENT_MIN_WIDTH}
            />
          </Stack>
        </Stack>

        <DataStatus
          isError={isError}
          isEmpty={isNilOrEmpty(columns)}
          isLoading={isLoading}
          minHeight={56}
        >
          <Show onNotebook onDesktop onTablet>
            <HStack spacing={2}>
              {columns.map((column) => (
                <Box flexBasis="100%" key={column.id}>
                  <Clickable
                    onClick={() => handleRedirectFromOpportunity(column.id)}
                    isDisabled={isRedirecting}
                  >
                    <HoverBox>
                      <VStack basis="100%" align="stretch" spacing={4}>
                        <VStack spacing={4} justify="space-between" grow={1}>
                          <HStack align="center" spacing={1}>
                            <Text color="secondary" size="xSmall">
                              {column.title}
                            </Text>
                            <Tooltip description={column.tooltip}>
                              <Icon value="action/info_outline" size={4} tabIndex={-1} />
                            </Tooltip>
                          </HStack>
                          <AnimatedFunnelValue value={column.value} />
                        </VStack>
                        <HStack height={SALES_FUNNEL_GRAPH_BAR_HEIGHT} grow={0} align="flex-end">
                          <motion.div
                            style={{width: '100%'}}
                            animate={{height: column.height}}
                            transition={{type: 'tween'}}
                          >
                            <Box flex={1} height="100%" backgroundColor={column.color} />
                          </motion.div>
                        </HStack>
                      </VStack>
                    </HoverBox>
                  </Clickable>
                </Box>
              ))}
            </HStack>
          </Show>

          <Show onMobile>
            <VStack spacing={1}>
              {columns.map((column) => (
                <Clickable
                  key={column.id}
                  onClick={() => handleRedirectFromOpportunity(column.id)}
                  isDisabled={isRedirecting}
                >
                  <HoverBox>
                    <VStack spacing={1}>
                      <HStack justify="space-between" align="center">
                        <HStack align="center" spacing={1}>
                          <Text color="secondary" size="xSmall">
                            {column.title}
                          </Text>
                          <Tooltip description={column.tooltip}>
                            <Icon value="action/info_outline" size={4} tabIndex={-1} />
                          </Tooltip>
                        </HStack>
                        <AnimatedFunnelValue value={column.value} />
                      </HStack>
                      <Box height={SALES_FUNNEL_GRAPH_BAR_HEIGHT_MOBILE}>
                        <motion.div
                          style={{height: '100%'}}
                          animate={{width: column.height}}
                          transition={{type: 'tween'}}
                        >
                          <Box height="100%" backgroundColor={column.color} />
                        </motion.div>
                      </Box>
                    </VStack>
                  </HoverBox>
                </Clickable>
              ))}
            </VStack>
          </Show>
        </DataStatus>
      </VStack>
    </Card>
  );
}

const ownerSegments: NonNullable<SegmentProps<OwnerSegment>['options']> = [
  {
    label: i18n.t('page.homepage.labels.total'),
    value: 'total',
  },
  {
    label: i18n.t('page.homepage.labels.my'),
    value: 'my',
  },
];

const typeSegments: NonNullable<SegmentProps<TypeSegment>['options']> = [
  {
    label: i18n.t('page.homepage.labels.selling'),
    value: 'selling',
  },
  {
    label: i18n.t('page.homepage.labels.buying'),
    value: 'buying',
  },
];

const HoverBox = styled.div`
  padding: ${getCssSize(2)};
  border-radius: ${({theme}) => theme.radii.medium};
  transition: background-color 0.2s ease-in-out;
  &:hover {
    background-color: ${({theme}) => theme.colors.palettes.neutral[40][40]};
  }
`;
